import { useCustom, type HttpError } from "@refinedev/core";
import type { RelationshipItemsResult } from "../types";

interface UseRelationshipItemsOptions {
  /** The source ItemType name (e.g., "LeaveRequest") */
  itemType: string;
  /** The source item ID */
  itemId: string;
  /** The relationship name (e.g., "LeaveRequest File") */
  relationshipName: string;
  /** Page number (default: 1) */
  page?: number;
  /** Page size (default: 25) */
  pageSize?: number;
  /** Whether to enable the query */
  enabled?: boolean;
}

interface UseRelationshipItemsResult {
  data: RelationshipItemsResult | undefined;
  isLoading: boolean;
  isError: boolean;
  error: HttpError | null;
  refetch: () => void;
}

/**
 * Hook to fetch relationship items for a given source item
 */
export const useRelationshipItems = ({
  itemType,
  itemId,
  relationshipName,
  page = 1,
  pageSize = 25,
  enabled = true,
}: UseRelationshipItemsOptions): UseRelationshipItemsResult => {
  const {
    data: responseData,
    isLoading,
    isError,
    error,
    refetch,
  } = useCustom<RelationshipItemsResult>({
    url: `/items/${itemType}/${itemId}/relationships/${encodeURIComponent(relationshipName)}`,
    method: "get",
    config: {
      query: {
        page,
        pageSize,
      },
    },
    queryOptions: {
      enabled: enabled && !!itemType && !!itemId && !!relationshipName,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  });

  // Extract data from Refine's CustomResponse wrapper
  const data = responseData?.data as RelationshipItemsResult | undefined;

  return {
    data,
    isLoading,
    isError,
    error: error || null,
    refetch,
  };
};
